<script setup>
import { inject, ref, reactive, computed, defineProps, onMounted } from 'vue';

// Node Modules
import { register } from 'swiper/element/bundle';
import 'swiper/element/css/effect-fade';

// Functions
import zeroPadding from '../functions/zeroPadding.js';

const mq = inject(['mq']);

register();

const props = defineProps({
    // ページID
    pageId: {
        type: String,
        default: 'frontpage'
    },
    // "mv_slide_01_pc.webp" であれば"mv"の部分
    fileName: {
        type: String,
        default: ''
    },
    // スライド数
    num: {
        type: Number,
        default: 0
    },
    // PC/SPで画像を出し分けるか
    devices: {
        type: Boolean,
        default: true
    },
    // 画像のalt
    alt: {
        type: String,
        default: ''
    },
    // スライド表示中に「is-animation」classを付与する
    animation: {
        type: Boolean,
        default: false
    },
});

// 要素の取得
const el = ref(null);
const swiper = ref(null);

// リアクティブデータの定義
const autoPlaying = ref(false); //自動再生しているかどうか
const speed = ref(1000); //スライドの切り替わりにかかる秒数
const currentEvents = reactive({}); //現在のスライドのswiperイベント
const beforeEl = ref(null); //遷移前のスライド要素
const currentEl = ref(null); //現在のスライド要素

// デバイス判定
const device = computed(() => {
    if (props.devices) {
        return mq.mdPlus ? '_pc' : '_sp';
    } else {
        return '';
    }
});

// スライドの枚数が1以下の場合ループモードをfalseにする（警告が出るため）
const loop = computed(() => {
    if (props.num < 2) return false;
    return true;
});

/**
 * アニメーション用のclass付与
 * */
const toggleAnimationClass = () => {
    if (!props.animation) return;

    // class付与
    currentEl.value.classList.add('is-animation');
    // console.log('add');
    
    // 前のスライドのclass削除
    if (!beforeEl.value || currentEl.value === beforeEl.value) return;
    setTimeout(() => {
        beforeEl.value.classList.remove('is-animation');
        // console.log('remove');
    }, speed.value);
}

// 自動再生制御
const autoPlayStart = () => {
    // swiperインスタンスにアクセスできない場合は常に自動再生（iOS15など）
    if (!swiper.value.swiper) {
        autoPlaying.value = true;
        return;
    }
    
    swiper.value.swiper.autoplay.stop(); //一旦自動再生を停止

    const doWhenScroll = () => {
        if (autoPlaying.value) return; //自動再生が開始していたら終了

        const scroll = window.scrollY;
        const rect = el.value.getBoundingClientRect();
        const targetPos = rect.top + scroll - window.innerHeight + 100;

        // スクロールが要素まで達したら自動再生を開始
        if (scroll > targetPos) {
            autoPlaying.value = true;
            swiper.value.swiper.autoplay.start();
            // console.log('init');
        }
    }
    window.addEventListener('load', doWhenScroll);
    window.addEventListener('scroll', doWhenScroll);
}

/**
 * スライドを移動
 * @var {Number} num スライド番号
 * */
const slideTo = (num) => {
    swiper.value.swiper.slideToLoop(num);
}

// スライドが切り替わった時に発火
const onSlideChange = (e) => {
    beforeEl.value = currentEl.value; //切り替わり前のスライドを取得
    Object.assign(currentEvents, e.detail[0]); //swiperイベントを取得
    currentEl.value = currentEvents.slides[currentEvents.activeIndex]; //切り替わり後のスライドを取得

    toggleAnimationClass();
}

onMounted(() => {
    autoPlayStart();
});
</script>

<template>
<div class="slider wow animate__fadeIn" ref="el">
    <swiper-container
    ref="swiper"
    class="slider__container"
    :speed="speed"
    autoplay-delay="4000"
    autoplay-disable-on-interaction="false"
    :loop="loop"
    effect="fade"
    @swiperslidechange="onSlideChange">
        <swiper-slide class="slider__slide" v-for="i in num" :key="`slide${i}`">
            <img :src="`/assets/img/${pageId}/${fileName}_slide_${zeroPadding(i) + device}.webp`" :alt="alt">
        </swiper-slide>
    </swiper-container>
    <ul class="slider__bar-list" v-if="num > 1">
        <li
        class="slider__bar-item"
        :class="{'is-current': currentEvents.realIndex === i-1 && autoPlaying}"
        @click="slideTo(i-1)"
        v-for="i in num"
        :key="`bar${i}`"></li>
    </ul>
</div>
</template>